import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SUBSIDIARIES } from "@/lib/content";
import { BRAND } from "@/lib/assets";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const solid = scrolled || !isHome || mobileOpen;
  const textColor = solid ? "text-navy" : "text-white";
  const barBg = solid
    ? "bg-white/95 backdrop-blur-lg border-b border-softgray shadow-[0_1px_0_rgba(15,42,92,0.04)]"
    : "bg-transparent";

  const navItems = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Sectors", to: "/sectors" },
    { label: "Why Us", to: "/why-us" },
    { label: "Governance", to: "/governance" },
    { label: "Contact", to: "/contact" },
  ];

  const linkClass = ({ isActive }) =>
    `editorial-link text-[13px] tracking-[0.14em] uppercase font-medium ${textColor} hover:text-gold ${isActive ? "!text-gold" : ""}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${barBg}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 flex items-center justify-between h-[72px] md:h-[84px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <img
            src={BRAND.logo}
            alt="Salihi Group logo"
            className={`h-9 md:h-11 w-auto transition-[filter] duration-500 ${solid ? "" : "brightness-0 invert"}`}
            loading="eager"
            decoding="async"
          />
          <span className="sr-only">Salihi Group</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {navItems.slice(0, 2).map((n) => (
            <NavLink key={n.label} to={n.to} end={n.to === "/"} className={linkClass}
                     data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, '-')}`}>
              {n.label}
            </NavLink>
          ))}

          {/* Companies dropdown */}
          <div className="relative"
               onMouseEnter={() => setCompaniesOpen(true)}
               onMouseLeave={() => setCompaniesOpen(false)}>
            <button
              className={`editorial-link inline-flex items-center gap-1.5 text-[13px] tracking-[0.14em] uppercase font-medium ${textColor} hover:text-gold`}
              data-testid="nav-companies"
              onClick={() => setCompaniesOpen((v) => !v)}
            >
              Our Companies
              <ChevronDown size={14} strokeWidth={1.75} />
            </button>
            {companiesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[380px]"
                   data-testid="companies-dropdown">
                <div className="bg-white border border-softgray shadow-[0_20px_60px_-15px_rgba(15,42,92,0.18)] p-3">
                  <Link to="/companies"
                        className="block px-4 py-2 mb-1 text-navy text-[11px] uppercase tracking-[0.24em] hover:text-gold border-b border-softgray">
                    View all companies
                  </Link>
                  {SUBSIDIARIES.map((s) => (
                    <Link key={s.slug} to={`/companies/${s.slug}`}
                          className="block px-4 py-3 hover:bg-offwhite transition-colors group border-l-2 border-transparent hover:border-gold"
                          data-testid={`nav-company-${s.slug}`}>
                      <div className="text-navy font-serif text-[15px] leading-tight">{s.name}</div>
                      <div className="text-mutedink text-xs mt-0.5">{s.tagline}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navItems.slice(2).map((n) => (
            <NavLink key={n.label} to={n.to} className={linkClass}
                     data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, '-')}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <Link to="/contact"
                className={solid
                  ? "btn-outline-gold"
                  : "btn-outline-gold !text-white !border-white/70 hover:!bg-white hover:!text-navy hover:!border-white"}
                data-testid="header-cta-contact">
            Get In Touch
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden ${textColor}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-softgray max-h-[calc(100vh-72px)] overflow-y-auto"
             data-testid="mobile-menu">
          <nav className="px-6 py-8 flex flex-col gap-1">
            {navItems.map((n) => (
              <Link key={n.label} to={n.to}
                    className="py-4 border-b border-softgray text-navy font-serif text-xl"
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, '-')}`}>
                {n.label}
              </Link>
            ))}
            <details className="py-2 border-b border-softgray group">
              <summary className="cursor-pointer text-navy font-serif text-xl py-2 list-none flex items-center justify-between">
                Our Companies
                <ChevronDown size={18} className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="pl-2 pt-2 pb-3 flex flex-col gap-3">
                <Link to="/companies" className="text-sm text-gold">View all companies →</Link>
                {SUBSIDIARIES.map((s) => (
                  <Link key={s.slug} to={`/companies/${s.slug}`} className="text-sm text-mutedink">
                    {s.name}
                  </Link>
                ))}
              </div>
            </details>
            <Link to="/contact" className="btn-gold mt-6 justify-center" data-testid="mobile-cta-contact">
              Get In Touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
